import { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';

interface UserLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

export const useLocationPermission = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [location, setLocation] = useState<UserLocation | null>(null);
  const [permissionGranted, setPermissionGranted] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  // Request location permission
  const requestLocationPermission = async (): Promise<boolean> => {
    if (!navigator.geolocation) {
      toast({
        title: "Location not supported",
        description: "Your device doesn't support location services",
        variant: "destructive",
      });
      return false;
    }

    return new Promise((resolve) => {
      setLoading(true);
      
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const userLocation: UserLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: Date.now()
          };
          
          setLocation(userLocation);
          setPermissionGranted(true);
          
          // Store location in localStorage for now (will be database when types are updated)
          localStorage.setItem('userLocation', JSON.stringify(userLocation));
          
          setLoading(false);
          resolve(true);
        },
        (error) => {
          console.error('Location permission denied:', error);
          setPermissionGranted(false);
          setLoading(false);
          
          toast({
            title: "Location permission needed",
            description: "Please enable location access for better delivery experience and accurate distance calculations",
            variant: "destructive",
          });
          
          resolve(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes cache
        }
      );
    });
  };

  // Get live location for orders
  const getLiveLocation = (): Promise<UserLocation | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const liveLocation: UserLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: Date.now()
          };
          resolve(liveLocation);
        },
        (error) => {
          console.error('Failed to get live location:', error);
          resolve(location); // Fallback to stored location
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 60000 // 1 minute cache for orders
        }
      );
    });
  };

  // Get stored location from localStorage
  const getStoredLocation = async (): Promise<UserLocation | null> => {
    const stored = localStorage.getItem('userLocation');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error('Error parsing stored location:', error);
        return null;
      }
    }
    return null;
  };

  // Load stored location on mount
  useEffect(() => {
    const loadStoredLocation = async () => {
      const stored = await getStoredLocation();
      if (stored) {
        setLocation(stored);
        setPermissionGranted(true);
      }
    };

    loadStoredLocation();
  }, []);

  return {
    location,
    permissionGranted,
    loading,
    requestLocationPermission,
    getLiveLocation,
    getStoredLocation
  };
};